/**
 * CustomerConformityAktionenTab — config wrapper for the shared ExporterAktionenTab.
 *
 * Provides customer-conformity-specific implementations of:
 *   - search          → api.customerSearch (full-text across downstream primitives)
 *   - fetchFreshness  → api.freshnessPartition (chain-walk from customer down to substance)
 *   - renderPreviewHtml → conformityLetterData + renderKundenKonformitaet
 *   - doExport        → generateKundenKonformitaetPdf
 *   - onStartExport   → seedExportDiff (populates diff view for pre-export editing)
 */

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useMemo } from "react"
import { api } from "../../../api/client"
import {
  renderKundenKonformitaet,
  type KundenKonformitaetHtmlData,
} from "../../../lib/templates/html/kunden_konformitaet"
import { generateKundenKonformitaetPdf } from "../../../lib/templates/pdf/kunden_konformitaet_pdf"
import type { ScopeDiffState } from "../../../types"
import type {
  ExporterFreshnessData,
  ExporterTabConfig,
} from "../shared/ExporterAktionenTab"
import { ExporterAktionenTab } from "../shared/ExporterAktionenTab"
import { seedExportDiff } from "../shared/seedExportDiff"

function todayFormatted(): string {
  return new Date().toLocaleDateString("de-DE")
}

function computeOverrides(
  diffs: Record<string, ScopeDiffState> | undefined,
): Record<string, Record<string, string>> | undefined {
  if (!diffs) return undefined
  const result: Record<string, Record<string, string>> = {}
  for (const sd of Object.values(diffs)) {
    for (const recDiff of Object.values(sd.records)) {
      for (const cell of Object.values(recDiff.cells)) {
        if (
          cell.fileValue !== null &&
          cell.fileValue !== undefined &&
          cell.fileValue !== cell.dbValue
        ) {
          result[recDiff.recordId] ??= {}
          result[recDiff.recordId][cell.primitiveName] = cell.fileValue
        }
      }
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

async function renderConformityHtml(
  recordId: string,
  diffs: Record<string, ScopeDiffState> | undefined,
): Promise<string | null> {
  const overrides = computeOverrides(diffs)
  const data = await api.conformityLetterData([recordId], overrides)
  const c = data[0]
  if (!c) return null
  const today = todayFormatted()
  const htmlData: KundenKonformitaetHtmlData = {
    issuer: "",
    issuerAddress: "",
    creationDate: today,
    signatory: "",
    signatoryRole: "",
    signatoryPlace: "",
    signatoryDate: today,
    customer: {
      customerId: c.customer_id,
      customerName: c.customer_name,
      isPositive: c.is_positive,
      totalProductCount: c.total_product_count,
      svhcProductCount: c.svhc_product_count,
      products: c.products.map((p) => ({
        productId: p.product_id,
        productName: p.product_name,
        rows: p.rows.map((r) => ({
          casNumber: r.cas_number,
          substanceName: r.substance_name,
          svhcGehalt: r.svhc_gehalt,
          locationInComponent: r.location_in_component,
          componentName: r.component_name,
        })),
      })),
    },
  }
  return renderKundenKonformitaet(htmlData)
}

async function doConformityExport(
  recordId: string,
  diffs: Record<string, ScopeDiffState> | undefined,
  noDataMessage: string,
): Promise<void> {
  const overrides = computeOverrides(diffs)
  const data = await api.conformityLetterData([recordId], overrides)
  const c = data[0]
  if (!c) throw new Error(noDataMessage)
  const today = todayFormatted()
  generateKundenKonformitaetPdf({
    issuer: "",
    issuerAddress: "",
    creationDate: today,
    signatory: "",
    signatoryRole: "",
    signatoryPlace: "",
    signatoryDate: today,
    customer: {
      customerId: c.customer_id,
      customerName: c.customer_name,
      isPositive: c.is_positive,
      totalProductCount: c.total_product_count,
      svhcProductCount: c.svhc_product_count,
      products: c.products.map((p) => ({
        productId: p.product_id,
        productName: p.product_name,
        rows: p.rows.map((r) => ({
          casNumber: r.cas_number,
          substanceName: r.substance_name,
          svhcGehalt: r.svhc_gehalt,
          locationInComponent: r.location_in_component,
          componentName: r.component_name,
        })),
      })),
    },
  })
}

export function CustomerConformityAktionenTab() {
  const { translate } = useTranslation()

  const config = useMemo<ExporterTabConfig>(
    () => ({
      apId: "customer-conformity",
      heading: translate("QMS/CUSTOMER_CONFORMITY/HEADING" as TranslationKeys),
      subheading: translate(
        "QMS/CUSTOMER_CONFORMITY/SUBHEADING" as TranslationKeys,
      ),
      scopeId: "customer",
      recordLabel: translate(
        "QMS/CUSTOMER_CONFORMITY/RECORD_LABEL" as TranslationKeys,
      ),

      // Cross-scope chain-walk search (overrides the shell's local default).
      search: (q) =>
        api.customerSearch(q).then((results) =>
          results.map((r) => ({
            record_id: r.customer_id,
            display_name: r.customer_name,
            matches: r.matches.map((m) => ({
              scope_id: m.scope_id,
              primitive_title: m.primitive_title,
              value: m.value,
            })),
            extra_match_count: Math.max(0, r.matches.length - 1),
          })),
        ),

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fetchFreshness: (threshold, _records): Promise<ExporterFreshnessData> =>
        api.freshnessPartition(threshold),

      renderPreviewHtml: renderConformityHtml,
      doExport: (recordId, diffs) =>
        doConformityExport(
          recordId,
          diffs,
          translate("QMS/CUSTOMER_CONFORMITY/NO_DATA" as TranslationKeys),
        ),
      onStartExport: (recordId) => seedExportDiff(recordId),

      cockpitTemplates: [
        {
          id: "kunden-konformitaet",
          title: translate(
            "QMS/CUSTOMER_CONFORMITY/TEMPLATE_TITLE" as TranslationKeys,
          ),
        },
      ],
      defaultFollowUpDays: 7,
      apOwnEmail: "kundenkonformitaet@ap.turnus.ai",
      subjectTemplateDefault: translate(
        "QMS/CUSTOMER_CONFORMITY/EMAIL_SUBJECT_DEFAULT" as TranslationKeys,
      ),
      bodyTemplateDefault: translate(
        "QMS/CUSTOMER_CONFORMITY/EMAIL_BODY_DEFAULT" as TranslationKeys,
      ),
    }),
    [translate],
  )

  return <ExporterAktionenTab config={config} />
}
